<template>
    <div class="timer" :class="{ 'timer-linear': layout === 'linear', 'timer-compact': layout === 'compact' }">
        <!-- Linear Layout -->
        <template v-if="layout === 'linear'">
            <div class="timer-item-linear timer-main-linear">
                <div class="timer-progress-background">
                    <div 
                        class="timer-progress-fill" 
                        :style="{ width: progressPercentage + '%' }"
                    ></div>
                </div>
                <div class="timer-content-linear">
                    <v-icon class="timer-icon-linear">mdi-clock-outline</v-icon>
                    <div class="timer-time-linear">{{ formattedTime }}</div>
                </div>
            </div>
            <div class="timer-item-linear">
                <v-icon class="timer-stat-icon-linear">mdi-package-variant</v-icon>
                <div class="timer-value-linear">{{ effectivePackstats.count || 0 }}</div>
            </div>
            <div class="timer-item-linear">
                <v-icon class="timer-stat-icon-linear">mdi-chart-line</v-icon>
                <div class="timer-value-linear">{{ formattedAverageTime }}</div>
            </div>
        </template>
        
        <!-- Compact Layout (default) -->
        <template v-else>
            <div class="timer-item timer-main">
                <div class="timer-progress-background">
                    <div 
                        class="timer-progress-fill" 
                        :style="{ width: progressPercentage + '%' }"
                    ></div>
                </div>
                <div class="timer-content">
                    <v-icon class="timer-icon">mdi-clock-outline</v-icon>
                    <div class="timer-time">{{ formattedTime }}</div>
                </div>
            </div>
            <div class="timer-info">
                <div class="timer-item">
                    <v-icon class="timer-stat-icon">mdi-package-variant</v-icon>
                    <div class="timer-value">{{ effectivePackstats.count || 0 }}</div>
                </div>
                <div class="timer-item">
                    <v-icon class="timer-stat-icon">mdi-chart-line</v-icon>
                    <div class="timer-value">{{ formattedAverageTime }}</div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import PacklistService from '@/services/PacklistService';
import { PackTracker } from '@/event-bus';

export default {
    name: 'Timer',
    props: {
        currentPackstats: {
            type: Object,
            default: () => ({
                progress: 0,
                targetProgress: 40,
                count: 0,
                time: 0,
                packStart: null
            })
        },
        layout: {
            type: String,
            default: 'compact',
            validator: value => ['linear', 'compact'].includes(value)
        }
    },
    data() {
        return {
            currentTime: Date.now(),
            sessionTimer: null,
            localPackstats: {
                progress: 0,
                targetProgress: 40,
                count: 0,
                time: 0,
                packStart: null,
                packEnd: null
            },
            packingStarted: false,
            itemViewStartTime: null
        }
    },
    computed: {
        // Use combined stats from props and local state
        effectivePackstats() {
            // Start with local timing data
            const combined = {
                progress: this.localPackstats.progress || 0,
                targetProgress: this.localPackstats.targetProgress || 40,
                packStart: this.localPackstats.packStart,
                packEnd: this.localPackstats.packEnd
            };
            
            // Always prioritize props for count and time (these come from API)
            combined.count = this.currentPackstats.count || this.localPackstats.count || 0;
            combined.time = this.currentPackstats.time || this.localPackstats.time || 0;
            
            // For packStart, prefer local (from EventBus/localStorage) over props
            if (!combined.packStart && this.currentPackstats.packStart) {
                combined.packStart = this.currentPackstats.packStart;
            }
            
            console.log('📊 Timer: effectivePackstats:', combined);
            console.log('📊 Timer: currentPackstats (props):', this.currentPackstats);
            console.log('📊 Timer: localPackstats:', this.localPackstats);
            
            return combined;
        },
        
        sessionTimeElapsed() {
            const packStart = this.effectivePackstats.packStart;
            if (!packStart) {
                console.log('📊 Timer: No packStart time available');
                return 0;
            }
            
            const now = new Date(this.currentTime);
            const startTime = new Date(packStart);
            const elapsed = Math.floor((now - startTime) / 1000);
            
            if (elapsed === 0) {
                console.log('📊 Timer: Zero elapsed time - packStart:', packStart, 'now:', now);
            }
            
            return elapsed;
        },
        
        progressPercentage() {
            const elapsed = this.sessionTimeElapsed;
            const target = this.effectivePackstats.targetProgress || 40;
            return Math.min((elapsed / target) * 100, 100);
        },
        
        formattedTime() {
            const elapsed = this.sessionTimeElapsed;
            const minutes = Math.floor(elapsed / 60);
            const seconds = elapsed % 60;
            const formatted = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
            
            if (formatted === '00:00' && this.effectivePackstats.packStart) {
                console.log('📊 Timer: Showing 00:00 but packStart exists:', this.effectivePackstats.packStart);
                console.log('📊 Timer: Current time:', new Date(this.currentTime));
                console.log('📊 Timer: Elapsed seconds:', elapsed);
            }
            
            return formatted;
        },
        
        formattedAverageTime() {
            const avgTime = this.effectivePackstats.time || 0;
            if (avgTime === 0) return '00:00';
            const minutes = Math.floor(avgTime / 60);
            const seconds = Math.round(avgTime % 60);
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
    },
    methods: {
        startSessionTimer() {
            this.sessionTimer = setInterval(() => {
                this.currentTime = Date.now();
            }, 1000);
        },
        
        stopSessionTimer() {
            if (this.sessionTimer) {
                clearInterval(this.sessionTimer);
                this.sessionTimer = null;
            }
        },

        // Initialize pack tracking session
        async initializePackSession(packlistItemId = null, userId = null) {
            try {
                // Check for existing session in localStorage
                const currentPackSession = localStorage.getItem('lastTimestamp');
                
                if (currentPackSession && userId) {
                    const packSession = JSON.parse(currentPackSession);
                    if (packSession.userId === userId) {
                        this.itemViewStartTime = new Date(packSession.packStart);
                        this.localPackstats.packStart = packSession.packStart;
                        console.log('📊 Timer: Using existing session start time', packSession.packStart);
                        return;
                    }
                }
                
                // Create new session
                const viewStartTime = new Date();
                this.itemViewStartTime = viewStartTime;
                
                const newPackSession = {
                    userId: userId,
                    packStart: viewStartTime,
                    packlistItemId: packlistItemId
                };

                this.localPackstats.packStart = newPackSession.packStart;
                localStorage.setItem('lastTimestamp', JSON.stringify(newPackSession));
                console.log('📊 Timer: Created new pack session', viewStartTime);
                
            } catch (error) {
                console.error('❌ Timer: Error initializing pack session:', error);
            }
        },

        // Initialize from localStorage
        async initializeFromLocalStorage() {
            try {
                const currentPackSession = localStorage.getItem('lastTimestamp');
                if (currentPackSession) {
                    const packSession = JSON.parse(currentPackSession);
                    this.localPackstats.packStart = packSession.packStart;
                    this.itemViewStartTime = new Date(packSession.packStart);
                    console.log('📊 Timer: Loaded from localStorage:', packSession);
                } else {
                    console.log('📊 Timer: No localStorage session found');
                }
            } catch (error) {
                console.error('❌ Timer: Error loading from localStorage:', error);
            }
        },

        // Load pack statistics
        async loadPackStats() {
            try {
                const response = await PacklistService.getPackStats();
                if (response?.data) {
                    this.localPackstats.count = response.data.count;
                    this.localPackstats.time = response.data.time;
                    console.log('📊 Timer: Loaded pack stats:', response.data);
                    console.log('📊 Timer: localPackstats updated to:', this.localPackstats);
                } else {
                    console.warn('📊 Timer: No pack stats data received:', response);
                }
            } catch (error) {
                console.error('❌ Timer: Error loading pack stats:', error);
            }
        }
    },
    async mounted() {
        this.startSessionTimer();
        
        // Initialize from localStorage first
        await this.initializeFromLocalStorage();
        
        // Load pack stats on mount
        await this.loadPackStats();
        
        // Listen for pack tracking events
        PackTracker.$on('pack-tracking-started', (data) => {
            console.log('📊 Timer: Pack tracking started event received:', data);
            console.log('📊 Timer: startTime type:', typeof data.startTime, data.startTime);
            this.packingStarted = true;
            this.localPackstats.packStart = data.startTime;
            this.itemViewStartTime = data.startTime;
            console.log('📊 Timer: Updated localPackstats.packStart to:', this.localPackstats.packStart);
            console.log('📊 Timer: effectivePackstats after update:', this.effectivePackstats);
        });
        
        PackTracker.$on('pack-tracking-ended', (data) => {
            console.log('📊 Timer: Pack tracking ended event received:', data);
            this.packingStarted = false;
            this.localPackstats.packEnd = data.endTime;
            // Reload stats after pack completion
            this.loadPackStats();
        });
        
        PackTracker.$on('pack-tracking-error', (data) => {
            console.error('📊 Timer: Pack tracking error:', data);
        });
    },
    
    beforeDestroy() {
        this.stopSessionTimer();
        
        // Clean up EventBus listeners
        PackTracker.$off('pack-tracking-started');
        PackTracker.$off('pack-tracking-ended');
        PackTracker.$off('pack-tracking-error');
    }
}
</script>

<style>
.timer {
    background-color: #f0f0f0;
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
    min-width: 180px;
}

.timer-item {
    background-color: #fff;
    border-radius: 6px;
    padding: 6px;
    position: relative;
}

.timer-main {
    position: relative;
    overflow: hidden;
}

.timer-progress-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
}

.timer-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #4caf50 0%, #8bc34a 100%);
    transition: width 0.3s ease;
}

.timer-content {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 2px 0;
}

.timer-icon {
    font-size: 1.2rem !important;
    color: #333;
}

.timer-time {
    font-size: 1.1rem;
    font-weight: bold;
    color: #000;
    font-family: 'Courier New', monospace;
}

.timer-info {
    display: flex;
    flex-direction: row;
    gap: 4px;
}

.timer-info .timer-item {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    text-align: center;
}

.timer-stat-icon {
    font-size: 1rem !important;
    color: #666;
}

.timer-value {
    font-size: 1.1rem;
    font-weight: bold;
    color: #000;
}

/* Linear Layout Styles */
.timer-linear {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    background-color: #f0f0f0;
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    padding: 8px 12px;
    min-width: auto;
}

.timer-item-linear {
    display: flex;
    align-items: center;
    gap: 6px;
    background-color: #fff;
    border-radius: 6px;
    padding: 6px 8px;
    position: relative;
    white-space: nowrap;
}

.timer-main-linear {
    position: relative;
    overflow: hidden;
    flex: 1;
    min-width: 80px;
}

.timer-content-linear {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.timer-icon-linear {
    font-size: 1rem !important;
    color: #333;
}

.timer-time-linear {
    font-size: 0.9rem;
    font-weight: bold;
    color: #000;
    font-family: 'Courier New', monospace;
}

.timer-stat-icon-linear {
    font-size: 0.9rem !important;
    color: #666;
}

.timer-value-linear {
    font-size: 0.9rem;
    font-weight: bold;
    color: #000;
}

/* Compact Layout Styles (default) */
.timer-compact {
    background-color: #f0f0f0;
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
    min-width: 180px;
}
</style>